import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * PaginationControls - Reusable pagination UI component
 *
 * Displays page navigation with:
 * - First/Previous/Next/Last buttons
 * - Page number buttons with ellipsis
 * - Jump to page input
 * - Result range info ("Showing 1-20 of 156")
 *
 * @param {Object} props
 * @param {number} props.currentPage - Current page (0-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} [props.totalElements] - Total number of items
 * @param {number} [props.pageSize] - Current page size
 * @param {boolean} props.hasNext - Whether there is a next page
 * @param {boolean} props.hasPrevious - Whether there is a previous page
 * @param {Function} props.onPageChange - Callback when page changes (receives page number)
 * @param {Function} props.onNext - Callback for next page
 * @param {Function} props.onPrevious - Callback for previous page
 * @param {boolean} [props.disabled] - Disable all controls
 * @param {string} [props.className] - Additional CSS class
 */
const PaginationControls = ({
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  hasNext,
  hasPrevious,
  onPageChange,
  onNext,
  onPrevious,
  disabled = false,
  className = '',
}) => {
  const [jumpValue, setJumpValue] = useState('');

  if (totalPages <= 0) return null;

  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(jumpValue, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum - 1);
      setJumpValue('');
    }
  };

  // Compute result range
  const rangeStart =
    totalElements > 0 && pageSize ? currentPage * pageSize + 1 : 0;
  const rangeEnd =
    totalElements > 0 && pageSize
      ? Math.min((currentPage + 1) * pageSize, totalElements)
      : 0;

  const renderPageNumbers = () => {
    const pages = [];

    // First page and ellipsis
    if (currentPage > 2) {
      pages.push(
        <button
          key="first"
          className="btn-secondary"
          onClick={() => onPageChange(0)}
          disabled={disabled}
        >
          1
        </button>
      );
      if (currentPage > 3) {
        pages.push(
          <span key="ellipsis-start" className="pagination-ellipsis">
            ...
          </span>
        );
      }
    }

    // Page numbers around current page
    const start = Math.max(0, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 3);

    for (let i = start; i < end; i++) {
      pages.push(
        <button
          key={i}
          className={i === currentPage ? 'btn-primary' : 'btn-secondary'}
          onClick={() => onPageChange(i)}
          disabled={disabled}
          style={{ minWidth: '40px' }}
        >
          {i + 1}
        </button>
      );
    }

    // Last page and ellipsis
    if (currentPage < totalPages - 3) {
      if (currentPage < totalPages - 4) {
        pages.push(
          <span key="ellipsis-end" className="pagination-ellipsis">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key="last"
          className="btn-secondary"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={disabled}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div
      className={`pagination-controls ${className}`}
      style={{ marginTop: '20px' }}
    >
      {/* Top row: result range + jump to page */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <span className="pagination-info">
          {totalElements > 0 && pageSize ? (
            <>
              Showing <strong>{rangeStart}</strong>&ndash;
              <strong>{rangeEnd}</strong> of <strong>{totalElements}</strong>{' '}
              results
            </>
          ) : (
            <>
              Page {currentPage + 1} of {totalPages}
            </>
          )}
        </span>

        <form
          onSubmit={handleJumpToPage}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <label
            htmlFor="jumpToPage"
            style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}
          >
            Go to page:
          </label>
          <input
            id="jumpToPage"
            type="number"
            min="1"
            max={totalPages}
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            placeholder={String(currentPage + 1)}
            disabled={disabled}
            style={{
              width: '64px',
              padding: '6px 10px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              textAlign: 'center',
            }}
          />
          <button
            type="submit"
            className="btn-secondary"
            disabled={disabled || !jumpValue}
            style={{ padding: '6px 12px', fontSize: '13px' }}
          >
            Go
          </button>
        </form>
      </div>

      {/* Bottom row: navigation buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          flexWrap: 'wrap',
        }}
      >
        <button
          className="btn-secondary"
          onClick={() => onPageChange(0)}
          disabled={!hasPrevious || disabled}
          style={{
            opacity: hasPrevious && !disabled ? 1 : 0.4,
            padding: '8px 10px',
            fontSize: '13px',
          }}
          title="First page"
        >
          &#171;
        </button>

        <button
          className="btn-secondary"
          onClick={onPrevious}
          disabled={!hasPrevious || disabled}
          style={{
            opacity: hasPrevious && !disabled ? 1 : 0.4,
            padding: '8px 12px',
            fontSize: '13px',
          }}
        >
          &larr; Prev
        </button>

        <div
          className="pagination-pages"
          style={{ display: 'flex', gap: '4px', margin: '0 8px' }}
        >
          {renderPageNumbers()}
        </div>

        <button
          className="btn-secondary"
          onClick={onNext}
          disabled={!hasNext || disabled}
          style={{
            opacity: hasNext && !disabled ? 1 : 0.4,
            padding: '8px 12px',
            fontSize: '13px',
          }}
        >
          Next &rarr;
        </button>

        <button
          className="btn-secondary"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={!hasNext || disabled}
          style={{
            opacity: hasNext && !disabled ? 1 : 0.4,
            padding: '8px 10px',
            fontSize: '13px',
          }}
          title="Last page"
        >
          &#187;
        </button>
      </div>
    </div>
  );
};

PaginationControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalElements: PropTypes.number,
  pageSize: PropTypes.number,
  hasNext: PropTypes.bool.isRequired,
  hasPrevious: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default PaginationControls;
